import { CircularProgress, Grid, Paper } from '@mui/material';
import { ColumnSizingState, Row, RowModel, RowSelectionState } from '@tanstack/react-table';
import { Formik, FormikProps } from 'formik';
import { GetContainersByPickUpParams, PickUpContainer } from 'modules/global/adapters/crpAPI';
import { DataGrid, Drawer } from 'modules/global/components';
import EmptyState from 'modules/global/components/EmptyState/EmptyState';
import { useLocalTableValues } from 'modules/global/hooks';
import React from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { errorMessage } from 'store/actions/messageActions';
import store from 'store/store';
import { useGetContainersUpdatePickUp } from '../adapters/update-pickup/queries';
import { ContainerDetailDrawer } from '../components/shared';
import { StepperUpdatePickUp } from '../components/update-pickup';
import { transportOperatorPickUpColumns } from '../constants/shared/transportOperatorPickUpColumns';

type Props = React.ComponentPropsWithoutRef<'div'> & {};

const initialFields: GetContainersByPickUpParams = {
  terminalNxtEntityId: '',
  meansOfTransport: '',
  creationDate: '',
  billOfLadingNumber: '',
  equipmentNumber: '',
};

const UpdatePickUp: React.FunctionComponent<Props> = () => {
  const { t } = useTranslation(['updatePickUp', 'common', 'validation']);
  const formRef = useRef<FormikProps<GetContainersByPickUpParams>>();

  const [displayGrid, setDisplayGrid] = React.useState(false);
  const [showDetail, setShowDetail] = React.useState<{
    open: boolean;
    data?: PickUpContainer;
  }>({
    open: false,
    data: undefined,
  });

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const getRowSelection = React.useRef<(() => RowModel<PickUpContainer>) | null>(null);
  const [selectedRows, setSelectedRows] = React.useState<Row<PickUpContainer>[] | null>(null);

  const [columnSizing, setColumnSizing] = useLocalTableValues<ColumnSizingState>(
    'column-sizing-assign-release-party',
    {}
  );

  const { data, isLoading, isFetching, isError } = useGetContainersUpdatePickUp({
    terminalNxtEntityId: formRef?.current?.values.terminalNxtEntityId,
    meansOfTransport: formRef?.current?.values.meansOfTransport,
    creationDate: formRef?.current?.values.creationDate,
    billOfLadingNumber: formRef?.current?.values.billOfLadingNumber,
    equipmentNumber: formRef?.current?.values.equipmentNumber,
  });

  const handleOnSubmit = () => {
    setDisplayGrid(true);
    setRowSelection({});
  };

  React.useEffect(() => {
    isError && store.dispatch(errorMessage(t('validation:errorGetContainers')));
  }, [isError, t]);

  React.useEffect(() => {
    if (getRowSelection.current !== null) {
      setSelectedRows(getRowSelection.current().rows);
    }
  }, [rowSelection]);

  const handleCellClick = (myRowData: PickUpContainer) => {
    setShowDetail({ open: true, data: myRowData });
  };

  return (
    <Grid container spacing={2} style={{ margin: '20px 0 0 5px' }}>
      <Grid
        size={{
          xs: 4,
          sm: 3,
        }}
      >
        <Formik
          initialValues={initialFields}
          innerRef={formRef as React.Ref<FormikProps<GetContainersByPickUpParams>>}
          onSubmit={handleOnSubmit}
        >
          {({
            errors,
            submitForm,
            resetForm,
            values,
          }: FormikProps<GetContainersByPickUpParams>) => {
            return (
              <StepperUpdatePickUp
                errors={errors}
                selectedRows={selectedRows}
                setRowSelection={setRowSelection}
                setDisplayGrid={setDisplayGrid}
                data={data}
                values={values}
                isFetching={isFetching}
                resetForm={resetForm}
                submitForm={submitForm}
              />
            );
          }}
        </Formik>
      </Grid>
      <Grid
        size={{
          xs: 8,
          sm: 9,
        }}
      >
        {displayGrid ? (
          isFetching ? (
            <CircularProgress />
          ) : isError ? (
            <EmptyState title={t('validation:errorGetContainers')} />
          ) : (
            <React.Fragment>
              <Paper>
                <DataGrid.Client
                  columns={transportOperatorPickUpColumns}
                  columnSizing={columnSizing}
                  data={data}
                  getRowSelection={getRowSelection}
                  isLoading={isLoading}
                  onCellClick={handleCellClick}
                  onColumnSizingChange={setColumnSizing}
                  onRowSelectionChange={setRowSelection}
                  rowSelection={rowSelection}
                />
              </Paper>
              <Drawer setShowDrawer={setShowDetail} showDrawer={showDetail.open}>
                {showDetail.data !== undefined && (
                  <ContainerDetailDrawer containerId={showDetail.data.releaseId} />
                )}
              </Drawer>
            </React.Fragment>
          )
        ) : (
          <EmptyState title={t('emptyState.title')} subTitle={t('emptyState.subTitle')} />
        )}
      </Grid>
    </Grid>
  );
};

export default UpdatePickUp;
